import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy as ContentCopyIcon,
  Devices as DevicesIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { mainApi } from '../../../config/apiClient';
import { useTranslation } from 'react-i18next';

interface DeviceScreen {
  id: number;
  screenCode: string;
  screenName: string;
  description?: string;
  screenType: string;
  kioskId?: number;
  status: 'pending' | 'active' | 'inactive';
  macAddress?: string;
  deviceModel?: string;
  androidVersion?: string;
  appVersion?: string;
  lastSeen?: string;
  activatedAt?: string;
  createdAt: string;
  isActive: boolean;
}

interface KioskDeviceTabProps {
  kioskId: string | null;
  companyId: string | null;
  kioskName?: string;
}

const KioskDeviceTab: React.FC<KioskDeviceTabProps> = ({ kioskId, companyId, kioskName }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width: 1499px)');
  const { t } = useTranslation('salesSystem');

  const [devices, setDevices] = useState<DeviceScreen[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newDeviceName, setNewDeviceName] = useState('');
  const [adding, setAdding] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingDevice, setDeletingDevice] = useState<DeviceScreen | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const loadDevices = useCallback(async () => {
    if (!companyId || !kioskId) return;

    setLoading(true);
    setError(null);
    try {
      const response = await mainApi.get(`/api/device/list?companyId=${companyId}&kioskId=${kioskId}`);
      const result = response.data;
      if (result.success && result.screens) {
        setDevices(result.screens);
      } else {
        setDevices([]);
      }
    } catch (err) {
      console.error('Cihaz listesi yükleme hatası:', err);
      setError(t('kioskDeviceTab.errors.listLoadFailed'));
    } finally {
      setLoading(false);
    }
  }, [companyId, kioskId]);

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  // Yeni cihaz ekle
  const handleAddDevice = async () => {
    if (!companyId || !kioskId) return;

    setAdding(true);
    try {
      const response = await mainApi.post('/api/device/add', {
        companyId: companyId,
        screenType: 'kiosk',
        kioskId: parseInt(kioskId),
        screenName: newDeviceName || t('kioskDeviceTab.defaults.deviceName', { kioskName: kioskName || t('kioskDeviceTab.defaults.kiosk') }),
        description: t('kioskDeviceTab.defaults.deviceDescription', { kioskName: kioskName || t('kioskDeviceTab.defaults.kiosk') })
      });

      const result = response.data;
      if (result.success) {
        setAddDialogOpen(false);
        setNewDeviceName('');
        await loadDevices();
      } else {
        setError(result.message || t('kioskDeviceTab.errors.addFailed'));
      }
    } catch (err) {
      console.error('Cihaz ekleme hatası:', err);
      setError(t('kioskDeviceTab.errors.addFailed'));
    } finally {
      setAdding(false);
    }
  };

  // Cihaz sil
  const handleDeleteDevice = async () => {
    if (!deletingDevice || !companyId) return;

    setDeleting(true);
    try {
      const response = await mainApi.post('/api/device/delete', {
        companyId: companyId,
        screenId: deletingDevice.id
      });

      const result = response.data;
      if (result.success) {
        setDeleteDialogOpen(false);
        setDeletingDevice(null);
        await loadDevices();
      } else {
        setError(result.message || t('kioskDeviceTab.errors.deleteFailed'));
      }
    } catch (err) {
      console.error('Cihaz silme hatası:', err);
      setError(t('kioskDeviceTab.errors.deleteFailed'));
    } finally {
      setDeleting(false);
    }
  };

  // Kodu kopyala
  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  // Durum chip'i
  const getStatusChip = (status: string) => {
    switch (status) {
      case 'active':
        return <Chip icon={<CheckCircleIcon />} label={t('kioskDeviceTab.status.active')} color="success" size="small" />;
      case 'pending':
        return <Chip icon={<PendingIcon />} label={t('kioskDeviceTab.status.pending')} color="warning" size="small" />;
      case 'inactive':
        return <Chip icon={<CancelIcon />} label={t('kioskDeviceTab.status.inactive')} color="error" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  // Tarih formatlama
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('tr-TR');
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Başlık ve Ekle Butonu */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <DevicesIcon color="primary" />
          <Typography variant="h6" fontWeight="bold">
            {t('kioskDeviceTab.title')}
          </Typography>
          <Chip label={t('kioskDeviceTab.deviceCount', { count: devices.length })} size="small" variant="outlined" />
        </Box>
        <Box display="flex" gap={1}>
          <Tooltip title={t('kioskDeviceTab.actions.refresh')}>
            <IconButton onClick={loadDevices} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            size="small"
            onClick={() => {
              setNewDeviceName(t('kioskDeviceTab.defaults.deviceName', { kioskName: kioskName || t('kioskDeviceTab.defaults.kiosk') }));
              setAddDialogOpen(true);
            }}
          >
            {t('kioskDeviceTab.actions.addDevice')}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Cihaz yoksa boş durum */}
      {devices.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2, border: '2px dashed', borderColor: 'divider' }}>
          <DevicesIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('kioskDeviceTab.empty.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {t('kioskDeviceTab.empty.description')}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setNewDeviceName(t('kioskDeviceTab.defaults.deviceName', { kioskName: kioskName || t('kioskDeviceTab.defaults.kiosk') }));
              setAddDialogOpen(true);
            }}
          >
            {t('kioskDeviceTab.actions.addFirstDevice')}
          </Button>
        </Paper>
      ) : (
        <>
          {/* Masaüstü: Tablo */}
          {!isMobile ? (
            <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ backgroundColor: 'primary.main', color: 'white', fontWeight: 'bold' }}>
                      {t('kioskDeviceTab.table.deviceName')}
                    </TableCell>
                    <TableCell sx={{ backgroundColor: 'primary.main', color: 'white', fontWeight: 'bold' }}>
                      {t('kioskDeviceTab.table.screenCode')}
                    </TableCell>
                    <TableCell sx={{ backgroundColor: 'primary.main', color: 'white', fontWeight: 'bold' }}>
                      {t('kioskDeviceTab.table.status')}
                    </TableCell>
                    <TableCell sx={{ backgroundColor: 'primary.main', color: 'white', fontWeight: 'bold' }}>
                      {t('kioskDeviceTab.table.deviceModel')}
                    </TableCell>
                    <TableCell sx={{ backgroundColor: 'primary.main', color: 'white', fontWeight: 'bold' }}>
                      {t('kioskDeviceTab.table.lastSeen')}
                    </TableCell>
                    <TableCell align="right" sx={{ backgroundColor: 'primary.main', color: 'white', fontWeight: 'bold' }}>
                      {t('kioskDeviceTab.table.actions')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {devices.map((device) => (
                    <TableRow key={device.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {device.screenName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Typography
                            variant="body2"
                            fontFamily="monospace"
                            fontWeight="bold"
                            sx={{ letterSpacing: 1 }}
                          >
                            {device.screenCode}
                          </Typography>
                          <Tooltip title={copiedCode === device.screenCode ? t('kioskDeviceTab.actions.copied') : t('kioskDeviceTab.actions.copy')}>
                            <IconButton size="small" onClick={() => handleCopyCode(device.screenCode)}>
                              <ContentCopyIcon fontSize="small" color={copiedCode === device.screenCode ? 'success' : 'action'} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell>{getStatusChip(device.status)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {device.deviceModel || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(device.lastSeen)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={t('kioskDeviceTab.actions.deleteDevice')}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setDeletingDevice(device);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            /* Mobil: Kart görünümü */
            <Box>
              {devices.map((device) => (
                <Card key={device.id} sx={{ mb: 1.5, borderRadius: 2 }}>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                      <Box>
                        <Typography variant="body1" fontWeight="bold">
                          {device.screenName}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                          <Typography
                            variant="body2"
                            fontFamily="monospace"
                            fontWeight="bold"
                            sx={{ letterSpacing: 1 }}
                          >
                            {device.screenCode}
                          </Typography>
                          <IconButton size="small" onClick={() => handleCopyCode(device.screenCode)}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        {getStatusChip(device.status)}
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setDeletingDevice(device);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">{t('kioskDeviceTab.table.deviceModel')}</Typography>
                        <Typography variant="body2">{device.deviceModel || '-'}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">{t('kioskDeviceTab.table.lastSeen')}</Typography>
                        <Typography variant="body2">{formatDate(device.lastSeen)}</Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </>
      )}

      {/* Cihaz Ekleme Modalı */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('kioskDeviceTab.addDialog.title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('kioskDeviceTab.addDialog.description')}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label={t('kioskDeviceTab.fields.deviceName')}
            value={newDeviceName}
            onChange={(e) => setNewDeviceName(e.target.value)}
            sx={{ mt: 1 }}
            placeholder={t('kioskDeviceTab.fields.deviceNamePlaceholder')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)} color="inherit" disabled={adding}>
            {t('kioskDeviceTab.actions.cancel')}
          </Button>
          <Button
            onClick={handleAddDevice}
            variant="contained"
            disabled={adding || !newDeviceName.trim()}
            startIcon={adding ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
          >
            {adding ? t('kioskDeviceTab.actions.adding') : t('kioskDeviceTab.actions.addDevice')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cihaz Silme Modalı */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('kioskDeviceTab.deleteDialog.title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('kioskDeviceTab.deleteDialog.description', { name: deletingDevice?.screenName || '' })}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('kioskDeviceTab.deleteDialog.screenCode')}: <strong>{deletingDevice?.screenCode}</strong>
          </Typography>
          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
            {t('kioskDeviceTab.deleteDialog.warning')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="inherit" disabled={deleting}>
            {t('kioskDeviceTab.actions.cancel')}
          </Button>
          <Button
            onClick={handleDeleteDevice}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
          >
            {deleting ? t('kioskDeviceTab.actions.deleting') : t('kioskDeviceTab.actions.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default KioskDeviceTab;
